import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type AdminSlackConnectionRow } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// One row per active container with a Slack workspace connected. "Boo"
// means the distributed Boo Slack app; "Custom" means the customer runs
// their own Slack app (container has its own slackAppId).
const columns: ColumnDef<AdminSlackConnectionRow>[] = [
  {
    accessorKey: "teamName",
    header: "Slack workspace name",
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.original.teamName ?? "—"}</span>
        {row.original.teamId && (
          <span className="text-xs text-muted-foreground font-mono">{row.original.teamId}</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "connectionType",
    header: "Type",
    cell: ({ row }) =>
      row.original.connectionType === "custom" ? (
        <Badge variant="secondary">Custom</Badge>
      ) : (
        <Badge variant="outline">Boo</Badge>
      ),
  },
  {
    accessorKey: "name",
    header: "Container",
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => row.original.organizationName ?? row.original.organizationId,
  },
  {
    id: "open",
    header: "",
    cell: ({ row }) => (
      <Button asChild variant="ghost" size="sm">
        <Link
          to="/orgs/$orgId/containers/$containerId/chat"
          params={{ orgId: row.original.organizationId, containerId: row.original.containerId }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink className="size-3.5" />
          Open
        </Link>
      </Button>
    ),
  },
];

export function AdminSlackConnectionsPage() {
  useDocumentTitle("Admin: Slack connections");
  // DB-backed (unlike the gchat view's live fan-out) — Slack connection
  // state lives in the DB, so a single query is authoritative. Cached 60s.
  const query = useQuery({
    queryKey: ["admin-slack-installed"],
    queryFn: api.admin.slackInstalled,
    staleTime: 60_000,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Slack"
          subtitle="Containers with a Slack workspace connected — distributed Boo Slack or a customer's own app."
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          {query.isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError || !query.data ? (
        <ErrorComponent error={query.error ?? "Failed to load"} />
      ) : (
        <div className="space-y-8">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              <strong className="text-foreground">{query.data.total}</strong> connected
            </span>
            <span>·</span>
            <span>
              <strong className="text-foreground">{query.data.boo}</strong> Boo Slack
            </span>
            <span>·</span>
            <span>
              <strong className="text-foreground">{query.data.custom}</strong> custom
            </span>
          </div>

          {query.data.connected.length > 0 ? (
            <DataTable columns={columns} data={query.data.connected} />
          ) : (
            <p className="text-sm text-muted-foreground">No containers have Slack connected.</p>
          )}
        </div>
      )}
    </div>
  );
}
